import * as React from 'react';
import {
  ForwardedRef,
  useEffect,
  useRef,
  useState,
  HTMLAttributes,
  cloneElement,
  ReactNode,
  CSSProperties,
} from 'react';
import Label from './Label';
import {cn} from '@/utils';
import {useTranslation} from 'react-i18next';

type ConditionalValue<T, M extends boolean> = M extends true ? T[] : T | null;

export type DropdownProps<T, M extends boolean = false> = {
  value: ConditionalValue<T, M>;
  onChange: (value: ConditionalValue<T, M>) => void;
  children: ReactNode;
  readOnly?: boolean;
  disabled?: boolean;
  label?: ReactNode;
  placeholder?: string;
  size?: 'md' | 'lg' | 'xl';
  width?: CSSProperties['width'];
  optionListHeight?: CSSProperties['height'];
  rightSlot?: ReactNode;
  bottomText?: ReactNode;
  type?: 'require' | 'optional' | 'filter';
  containerClassName?: string;
  multiple?: M;
  isUndefinedShowAll?: boolean;
} & Omit<
  HTMLAttributes<HTMLButtonElement>,
  'disabled' | 'value' | 'type' | 'onChange'
>;

const sizeClasses = {
  md: 'h-[2.75rem]',
  lg: 'h-[3.125rem]',
  xl: 'h-[3.375rem]',
};
const optionTriggerClasses = {
  default: 'border border-neutral-300 cursor-pointer',
  disabled:
    'border border-neutral-300 cursor-not-allowed focus:border-neutral-300',
  readOnly: 'border border-neutral-300 cursor-default focus:border-neutral-300',
  opened: 'border border-primary',
};
const OptionClasses = {
  default: 'text-neutral-900 bg-transparent cursor-pointer',
  disabled: 'text-neutral-500 bg-transparent cursor-not-allowed',
  readOnly: 'text-neutral-900 bg-transparent cursor-default',
};

export function Dropdown<T, M extends boolean = false>(
  props: DropdownProps<T, M>,
) {
  const {
    value,
    onChange,
    children,
    placeholder,
    disabled,
    readOnly,
    label,
    bottomText,
    size = 'lg',
    width = '100%',
    optionListHeight = 300,
    rightSlot = 'default',
    className,
    containerClassName,
    style,
    type = 'require',
    multiple = false as M,
    isUndefinedShowAll = false,
    ...rest
  } = props;
  const {t} = useTranslation();
  const selectRef = useRef<HTMLDivElement>(null);
  const optionListRef = useRef<HTMLDivElement>(null);
  const childrenArr = React.Children.toArray(children) as React.ReactElement[];

  const [isOpen, setIsOpen] = useState(false);
  const [openUpwards, setOpenUpwards] = useState(false);

  const selectedOptions = multiple
    ? childrenArr.filter(child => (value as T[]).includes(child.props.value))
    : childrenArr.find(child => child.props.value === value);

  const handleClose = () => {
    if (!multiple) {
      setIsOpen(false);
    }
  };

  const handleToggle = () => {
    if (disabled || readOnly) return;
    setIsOpen(!isOpen);
  };

  const handleOutsideClick = (e: MouseEvent) => {
    if (
      isOpen &&
      selectRef.current &&
      !selectRef.current.contains(e.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && selectRef.current && optionListRef.current) {
      const dropdownRect = selectRef.current.getBoundingClientRect();
      const optionListRect = optionListRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      if (dropdownRect.bottom + optionListRect.height > viewportHeight + 20) {
        setOpenUpwards(true);
      } else {
        setOpenUpwards(false);
      }
    }
  }, [isOpen]);

  const selectedOptionLabel = multiple
    ? (selectedOptions as React.ReactElement[])
        .map(option => option.props.children)
        .join(', ')
    : (selectedOptions as React.ReactElement)?.props.children;

  return (
    <div className={cn('flex flex-col gap-[0.375rem]', containerClassName)}>
      {label != null ? <Label>{label}</Label> : null}
      <div className='flex flex-col w-full' style={{width}} ref={selectRef}>
        <OptionListTrigger
          handleToggle={handleToggle}
          isOpen={isOpen}
          size={size}
          readOnly={readOnly}
          className={cn(
            (multiple && (value as T[]).length > 0) ||
              (!multiple && value != null)
              ? ''
              : 'text-[#9F9F9F]',
            className,
          )}
          {...rest}>
          {selectedOptionLabel != null
            ? selectedOptionLabel
            : isUndefinedShowAll
              ? t('common.all')
              : placeholder || t('common.select')}
          {rightSlot != null ? (
            <div>
              {rightSlot === 'default' ? (
                <DefaultRightSlot
                  isOpen={isOpen}
                  disabled={disabled || readOnly}
                />
              ) : (
                rightSlot
              )}
            </div>
          ) : null}
        </OptionListTrigger>
        <div className='relative'>
          <OptionList
            ref={optionListRef}
            height={optionListHeight}
            openUpwards={openUpwards}
            isOpen={isOpen}>
            {/* 리셋 상태를 위한 option */}
            {type !== 'require' && (
              <Dropdown.Option<T, M>
                value={multiple ? ([] as unknown as T) : (null as unknown as T)}
                onClick={() => {
                  onChange(
                    multiple
                      ? ([] as unknown as ConditionalValue<T, M>)
                      : (null as ConditionalValue<T, M>),
                  );
                  handleClose();
                }}>
                {type === 'filter' ? t('common.all') : t('common.select')}
              </Dropdown.Option>
            )}
            {childrenArr.map(children => {
              return cloneElement(children, {
                key: children.props.value,
                size,
                onChange,
                handleClose,
                readOnly,
                multiple,
                isSelected: multiple
                  ? (value as T[]).includes(children.props.value)
                  : value === children.props.value,
              });
            })}
          </OptionList>
        </div>
      </div>
    </div>
  );
}

Dropdown.displayName = 'Dropdown';

type OptionListTriggerType = {
  className?: string;
  disabled?: boolean;
  readOnly?: boolean;
  isOpen: boolean;
  size: 'md' | 'lg' | 'xl';
  handleToggle: () => void;
  children: ReactNode;
} & Omit<HTMLAttributes<HTMLButtonElement>, 'disabled' | 'readOnly'>;
const OptionListTrigger = ({children, ...props}: OptionListTriggerType) => {
  const {className, disabled, readOnly, isOpen, size, handleToggle, ...rest} =
    props;

  const variant = disabled
    ? 'disabled'
    : readOnly
      ? 'readOnly'
      : isOpen
        ? 'opened'
        : 'default';

  return (
    <button
      type='button'
      className={cn([
        'relative flex justify-between items-center p-4 rounded-[0.5rem] bg-white overflow-hidden outline-none',
        readOnly ? 'cursor-default' : 'cursor-pointer',
        readOnly ? '' : 'focus:border-primary',
        optionTriggerClasses[variant],
        sizeClasses[size],
        className,
      ])}
      {...rest}
      onClick={handleToggle}>
      {children}
    </button>
  );
};

const DefaultRightSlot = ({
  isOpen,
  disabled = false,
}: {
  isOpen: boolean;
  disabled?: boolean;
}) => {
  return (
    <span
      style={{
        display: 'flex',
      }}>
      <svg
        style={{transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}}
        width='1.25rem'
        height='1.25rem'
        viewBox='0 0 20 20'
        xmlns='http://www.w3.org/2000/svg'>
        <g fill='none' fillRule='evenodd'>
          <path d='M0 0h20v20H0z' />
          <path
            stroke={disabled ? '#cfcfcf' : '#9f9f9f'}
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
            d='m15 8-4.5 5L6 8'
          />
        </g>
      </svg>
    </span>
  );
};

export type OptionListProps = {
  isOpen: boolean;
  height: CSSProperties['height'];
  openUpwards: boolean;
} & HTMLAttributes<HTMLDivElement>;

const OptionList = React.forwardRef<HTMLDivElement, OptionListProps>(
  ({height, openUpwards, isOpen, children, className, ...props}, ref) => {
    if (!isOpen) return null;

    return (
      <div
        ref={ref}
        className={cn([
          'absolute p-0 m-0 w-full shadow-[0_2px_12px_0px_rgba(0,0,0,0.12)] border-none overflow-auto bg-white rounded-lg whitespace-nowrap z-50',
          openUpwards ? 'bottom-full mb-[3.75rem]' : 'top-full mt-[0.625rem]',
          className,
        ])}
        style={{
          display: isOpen ? 'block' : 'none',
          maxHeight: height,
          width: 'max-content',
          minWidth: '100%',
        }}
        {...props}>
        {children}
      </div>
    );
  },
);

export type DropdownOptionProps<T, M extends boolean = false> = {
  value: T;
  children: React.ReactNode;
  hoverColor?: string;
  disabled?: boolean;
  readOnly?: boolean;
  size?: 'md' | 'lg' | 'xl';
  onChange?: (value: ConditionalValue<T, M>) => void;
  handleClose?: () => void;
  multiple?: M;
  isSelected?: boolean;
} & Omit<HTMLAttributes<HTMLDivElement>, 'disabled' | 'value'>;

function DropdownOption<T, M extends boolean = false>(
  props: DropdownOptionProps<T, M>,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const {
    value,
    children,
    hoverColor,
    disabled,
    readOnly,
    onChange,
    handleClose,
    size = 'lg',
    multiple = false as M,
    isSelected = false,
    ...rest
  } = props;

  const handleOptionClick = () => {
    if (disabled || readOnly) return;
    if (multiple) {
      onChange?.(((prev: T[]) =>
        isSelected
          ? prev.filter(v => v !== value)
          : [...prev, value]) as ConditionalValue<T, M>);
    } else {
      onChange?.(value as ConditionalValue<T, M>);
      handleClose?.();
    }
  };

  const variant = readOnly ? 'readOnly' : disabled ? 'disabled' : 'default';

  return (
    <div
      className={cn(
        'flex justify-start items-center py-0 px-4 m-0 w-full border-0 font-normal',
        sizeClasses[size],
        OptionClasses[variant],
        readOnly ? 'hover:bg-none' : 'hover:bg-neutral-100',
        isSelected ? 'bg-neutral-100' : '',
      )}
      onClick={handleOptionClick}
      ref={ref}
      {...rest}>
      {children}
      {multiple && isSelected && (
        <svg
          className='ml-auto'
          xmlns='http://www.w3.org/2000/svg'
          width='20'
          height='20'
          viewBox='0 0 20 20'
          fill='none'>
          <path
            d='M4.28564 10.273L7.9302 14.2863L15.7142 5.71484'
            stroke='black'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      )}
    </div>
  );
}

Dropdown.Option = React.forwardRef(DropdownOption) as <
  T,
  M extends boolean = false,
>(
  props: DropdownOptionProps<T, M> & {ref?: ForwardedRef<HTMLDivElement>},
) => ReturnType<typeof DropdownOption>;

export default Dropdown;
